import { apiFetch } from "@shared/api/api";
import { global } from "@shared/libs/Global";
import { ApiError } from "@shared/type/ApiType";

export const DEFAULT_TENANT_ID = "DEFAULT";
export const DEFAULT_TENANT_NAME = "지투시스넷";

type HydrateSessionParams = {
  /** X-TENANT-ID 헤더로 보낼 값 */
  tenantId: string;
  /**
   * userInfo.tenant_id 로 저장할 값.
   * 로그인 흐름은 BE 응답의 tenantId를 우선하므로 헤더 값과 다를 수 있다.
   */
  storeTenantId?: string;
  tenantName?: string;
  dcIp?: string;
};

/**
 * 유효한 세션을 전제로 사용자/시스템 설정을 받아 전역 스토어를 채운다.
 *
 * 일반 로그인(UseAuthService)과 텔레그램 SSO 진입(ShareLanding)이 같은 경로를 쓰도록 분리했다.
 * SSO로 들어온 브라우저는 스토어가 비어 있어서, 이 과정을 거치지 않으면
 * X-TENANT-ID가 DEFAULT로 굳고 메뉴/화면이 cd_firm·cd_user 없이 동작하게 된다.
 */
export const hydrateSession = async ({
  tenantId,
  storeTenantId,
  tenantName,
  dcIp,
}: HydrateSessionParams) => {
  const response = await apiFetch("/login/after-process", {
    method: "POST",
    body: {} as any,
    headers: {
      "X-TENANT-ID": tenantId,
    },
  });

  // 로그인 이후 사용자/시스템 설정을 불러온다
  if (response.code != 200) {
    throw ApiError.badRequest("서버 오류가 발생하였습니다.");
  }

  const { bookMark, systemConfig, userConfig } = response.data as any;

  const systemConfigData = (systemConfig ?? []).reduce(
    (acc: Record<string, any>, curr: any) => {
      if (curr?.cd_ctrl) acc[curr.cd_ctrl] = curr;
      return acc;
    },
    {}
  );

  const bookmarkData = (bookMark ?? []).reduce(
    (acc: Record<string, boolean>, item: any) => {
      if (item?.cd_menu) acc[item.cd_menu] = true;
      return acc;
    },
    {}
  );

  const { setUserInfo, setConfig, setBookmark } = global.getState();

  setUserInfo({
    ...userConfig,
    dc_ip: dcIp ?? "",
    tenant_id: storeTenantId || tenantId || DEFAULT_TENANT_ID,
    tenant_name: tenantName || DEFAULT_TENANT_NAME,
    loginTime: new Date().toISOString(),
  });
  setConfig(systemConfigData);
  setBookmark(bookmarkData);

  return userConfig;
};

/** 클라이언트 IP 조회. 실패해도 로그인/진입을 막지 않는다. */
export const fetchClientIp = async (): Promise<string> => {
  try {
    const response = await fetch("https://ipinfo.io/json");
    const data = await response.json();
    return data?.ip ?? "";
  } catch (error) {
    console.error("Error fetching IP address:", error);
    return "";
  }
};

/** 테넌트 표시명 조회(화면 타이틀용). 실패 시 빈 문자열. */
export const fetchTenantName = async (tenantId: string): Promise<string> => {
  try {
    const response = await apiFetch("/tenant/info", {
      method: "GET",
      headers: {
        "X-TENANT-ID": tenantId,
      },
    });
    if (response.code === 200) {
      return (response.data as any)?.tenantName || "";
    }
  } catch (error) {
    console.error("tenant info fetch failed", error);
  }
  return "";
};
